import { MutationFunction } from '@apollo/client'
import { Autocomplete, TextField } from '@mui/material'
import { GetCandidatePreferencesQuery } from '__generated__/graphql'
import { FC, useState } from 'react'
import UpdatedText from 'shared/components/UpdatedText/UpdatedText'
import { useEffectOnlyAfterMount } from 'shared/hooks/useEffectAfterMount'
import { DropdownOption } from 'shared/models/dropdownOption'
import { getPxAsRem } from 'shared/utils'

const INDUSTRY_PREFERENCE_LIMIT = 3

interface IndustryProps {
    industryOptions: DropdownOption[]
    updatedAt: string
    updatedBy: string
    candidateId?: string
    handleUpdate: MutationFunction<any, any>
    industryPreferences: NonNullable<
        GetCandidatePreferencesQuery['candidate']
    >['industryPreferences']
}

const Industry: FC<IndustryProps> = ({
    industryPreferences,
    industryOptions,
    updatedAt,
    updatedBy,
    handleUpdate,
    candidateId,
}) => {
    const [primaryIndustries, setPrimaryIndustries] = useState<
        DropdownOption[]
    >(
        industryPreferences
            ?.filter((industry) => industry.order === 1)
            .map((industry) => industry.preferenceIndustry) || []
    )
    const [secondaryIndustries, setSecondaryIndustries] = useState<
        DropdownOption[]
    >(
        industryPreferences
            ?.filter((industry) => industry.order === 2)
            .map((industry) => industry.preferenceIndustry) || []
    )
    const [tertiaryIndustries, setTertiaryIndustries] = useState<
        DropdownOption[]
    >(
        industryPreferences
            ?.filter((industry) => industry.order === 3)
            .map((industry) => industry.preferenceIndustry) || []
    )

    const isOptionsDisabled = (option, selectedOptions): boolean => {
        return (
            selectedOptions?.length >= 3 &&
            !selectedOptions.some((item) => item.value === option.value)
        )
    }

    const handleSave = () => {
        handleUpdate({
            variables: {
                candidateId,
                preferences: [
                    ...primaryIndustries.map((industry) => ({
                        preferenceIndustryId: industry.id,
                        order: 1,
                    })),
                    ...secondaryIndustries.map((industry) => ({
                        preferenceIndustryId: industry.id,
                        order: 2,
                    })),
                    ...tertiaryIndustries.map((industry) => ({
                        preferenceIndustryId: industry.id,
                        order: 3,
                    })),
                ],
            },
        })
    }

    useEffectOnlyAfterMount(() => {
        handleSave()
    }, [primaryIndustries, secondaryIndustries, tertiaryIndustries])

    return (
        <>
            <Autocomplete
                multiple
                fullWidth
                disableClearable
                sx={{ marginBottom: getPxAsRem(10) }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(event, newValue) => {
                    if (newValue.length <= INDUSTRY_PREFERENCE_LIMIT)
                        setPrimaryIndustries(newValue)
                }}
                value={primaryIndustries}
                options={industryOptions.filter(
                    (industry) =>
                        secondaryIndustries
                            .concat(tertiaryIndustries)
                            .map((industry) => industry.id)
                            .indexOf(industry.id) === -1
                )}
                getOptionLabel={(option) => option.value}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Primary Industry"
                        placeholder=""
                    />
                )}
                getOptionDisabled={(option) =>
                    isOptionsDisabled(option, primaryIndustries)
                }
            />
            <Autocomplete
                multiple
                fullWidth
                disableClearable
                sx={{ marginBottom: getPxAsRem(10) }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(event, newValue) => {
                    if (newValue.length <= INDUSTRY_PREFERENCE_LIMIT)
                        setSecondaryIndustries(newValue)
                }}
                value={secondaryIndustries}
                options={industryOptions.filter(
                    (industry) =>
                        primaryIndustries
                            .concat(tertiaryIndustries)
                            .map((industry) => industry.id)
                            .indexOf(industry.id) === -1
                )}
                getOptionLabel={(option) => option.value}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Secondary Industry"
                        placeholder=""
                    />
                )}
                getOptionDisabled={(option) =>
                    isOptionsDisabled(option, secondaryIndustries)
                }
            />
            <Autocomplete
                multiple
                disableClearable
                fullWidth
                sx={{ marginBottom: getPxAsRem(10) }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(event, newValue) => {
                    if (newValue.length <= INDUSTRY_PREFERENCE_LIMIT)
                        setTertiaryIndustries(newValue)
                }}
                value={tertiaryIndustries}
                options={industryOptions.filter(
                    (industry) =>
                        secondaryIndustries
                            .concat(primaryIndustries)
                            .map((industry) => industry.id)
                            .indexOf(industry.id) === -1
                )}
                getOptionLabel={(option) => option.value}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Tertiary Industry"
                        placeholder=""
                    />
                )}
                getOptionDisabled={(option) =>
                    isOptionsDisabled(option, tertiaryIndustries)
                }
            />
            <UpdatedText updatedAt={updatedAt} updatedBy={updatedBy} />
        </>
    )
}

export default Industry
